import React from 'react'

import Accordion from '@/app/component/atom/accordion'
import { cannot } from '@/app/helpers'
import CampaignContentSMS from '@/app/module/campaigns/component/item/steps/content/sms'
import CampaignContentVoice from '@/app/module/campaigns/component/item/steps/content/voice'
import { BaseLanguageType } from '@/app/module/campaigns/helpers'
import {
  CampaignFileResult,
  CampaignTemplateType,
  Language,
  SMSMessage,
  VoiceMessage,
} from '@/app/module/campaigns/types'
import { FilesType, FileUploadType, PersonalizationType } from '@/app/types'

import type { WelcomeChange } from './utils/welcome'

type Props = {
  campaignType: CampaignTemplateType
  content?: boolean
  defaultLanguage: string
  files: {
    data: FilesType
    loadingItems: string[]
  }
  hasQuestion?: boolean
  id: string
  languages: Language[]
  loading: boolean
  personalizationList: PersonalizationType
  voiceList: BaseLanguageType[]
  voicePlayingId?: string
  welcome: WelcomeChange | undefined

  getFile: (id: string, file: string) => Promise<CampaignFileResult>
  onChange: (value: WelcomeChange) => void
  onMicAccessError: () => void
  saveFile: (id: string, file: FileUploadType) => Promise<CampaignFileResult>
  setPlayingId: (playId?: string) => void
}

const emptyObject = {}

const WelcomeMessage: React.FC<Props> = ({
  campaignType,
  content,
  defaultLanguage,
  files,
  hasQuestion,
  id,
  languages,
  loading,
  personalizationList,
  voiceList,
  voicePlayingId,
  welcome,
  getFile,
  onChange,
  onMicAccessError,
  saveFile,
  setPlayingId,
}) => {
  const getFileHandler = React.useCallback((file: string) => getFile(id, file), [id, getFile])
  const saveFileHandler = React.useCallback((item: FileUploadType) => saveFile(id, item), [id, saveFile])

  switch (campaignType) {
    case CampaignTemplateType.CATI:
    case CampaignTemplateType.Drip:
    case CampaignTemplateType.Reminder:
    case CampaignTemplateType.SMSBlast:
    case CampaignTemplateType.WhatsAppSurvey:
    case CampaignTemplateType.Chatbot:
      return null

    case CampaignTemplateType.SMSSurvey: {
      const info = hasQuestion
        ? 'Include the choices in your question, e.g. "What is your gender? Reply 1 for female, 2 for male"'
        : 'Add your message'
      return (
        <WelcomeContainer content={content}>
          <CampaignContentSMS
            defaultLanguage={defaultLanguage}
            info={info}
            item={welcome as SMSMessage}
            label="Message"
            languages={languages}
            personalizationList={personalizationList}
            changeHandler={onChange}
          />
        </WelcomeContainer>
      )
    }

    case CampaignTemplateType.VoiceBlast:
    case CampaignTemplateType.VoiceSurvey: {
      const info = hasQuestion
        ? 'Include the answer choices in your question, e.g. “What is your gender? Press the number 1 on your phone for female, press the number 2 for male."'
        : ''
      return (
        <WelcomeContainer content={content}>
          <CampaignContentVoice
            debounceChange
            defaultLanguage={defaultLanguage}
            enableTTS
            enableTranscription
            files={files.data || emptyObject}
            filterType={campaignType}
            info={info}
            item={welcome as VoiceMessage}
            languages={languages}
            loading={loading}
            personalizationList={personalizationList}
            playId={voicePlayingId}
            voiceList={voiceList}
            changeHandler={onChange}
            getFile={getFileHandler}
            onMicAccessError={onMicAccessError}
            saveFileHandler={saveFileHandler}
            setPlaying={setPlayingId}
          />
        </WelcomeContainer>
      )
    }

    default:
      return cannot(campaignType)
  }
}

type ContainerProps = {
  children: NonNullable<React.ReactNode>
  content?: boolean
}

const WelcomeContainer: React.FC<ContainerProps> = ({ children, content }) => {
  return (
    <div
      style={{
        marginBottom: '18px',
      }}
    >
      <Accordion
        data-testid="welcome-accordion"
        display={!!content}
        placeholder={`Click here to ${content ? 'edit the' : 'add a'} welcome message.`}
        title="Welcome Message"
      >
        {children}
      </Accordion>
    </div>
  )
}

export default React.memo(WelcomeMessage)
